import type { ChosenFile } from "@nexxt/api-client";
import type { auditEntryDto, CursorPage, MatchingPage, Page, WithAnyOfText } from "@nexxt/contracts";
import type {
  AddProductImagesInput,
  AttributeTaskActionInput,
  attributeCompletenessDto,
  attributeOptionsDto,
  attributeTaskDto,
  ChangeLineCarryoverInput,
  CheckLinesheetInput,
  CreateLinesheetInput,
  DownloadProductImageInput,
  GetAttributeTaskInput,
  GetLinesheetHistoryInput,
  GetLinesheetInput,
  ImportLinesheetLinesInput,
  ImportSkuAttributesInput,
  importBatchDto,
  importErrorDto,
  LinesheetActionInput,
  ListAttributeCompletenessInput,
  ListAttributeTasksInput,
  ListCarryoverCandidatesInput,
  ListImportErrorsInput,
  ListImportsInput,
  ListLinesheetLinesInput,
  ListLinesheetsInput,
  ListProductImagesInput,
  ListTaskSkusInput,
  lineOptionsDto,
  linesheetCheckDto,
  linesheetLineDto,
  linesheetOptionsDto,
  linesheetSummaryDto,
  productImagesDto,
  productListItemDto,
  RemoveLinesheetLinesInput,
  RemoveProductImageInput,
  ReorderProductImagesInput,
  RequestLineDeletionInput,
  SaveLinesheetLinesInput,
  SaveSkuAttributesInput,
  skuAttributesDto,
  taskSkusDto,
  UpdateLinesheetInput,
} from "@nexxt/contracts/catalog";
import type * as z from "zod";

// What the catalog screens ask of a gateway (ADR-166): one call per operation of the catalog, with the input and the
// answer of its contract. Each app gives its own, on the client of its gateway, and the screens never see a route.

export type LinesheetSummary = z.output<typeof linesheetSummaryDto>;
export type LinesheetOptions = z.output<typeof linesheetOptionsDto>;
type LinesheetCheck = z.output<typeof linesheetCheckDto>;
export type LinesheetLine = z.output<typeof linesheetLineDto>;
export type LineOptions = z.output<typeof lineOptionsDto>;
type CarryoverCandidate = z.output<typeof productListItemDto>;
export type AttributeTask = z.output<typeof attributeTaskDto>;
export type AttributeCompleteness = z.output<typeof attributeCompletenessDto>;
export type SkuAttributes = z.output<typeof skuAttributesDto>;
export type AttributeOptions = z.output<typeof attributeOptionsDto>;
type TaskSkus = z.output<typeof taskSkusDto>;
export type ImportBatch = z.output<typeof importBatchDto>;
export type ImportError = z.output<typeof importErrorDto>;
type AuditEntry = z.output<typeof auditEntryDto>;
export type ProductImages = z.output<typeof productImagesDto>;
export type ProductImage = ProductImages["images"][number];

/** The input of an operation as the screens give it: the paging the contract coerces (`page`, `limit`) as numbers. */
type Given<I> = { [K in keyof I]: unknown extends I[K] ? number | undefined : I[K] };
type Read<I, R> = (input: Given<I>, signal?: AbortSignal) => Promise<R>;
type Write<I, R> = (input: I) => Promise<R>;

/** The operations of the catalog screens, by the names of the catalog's RPC methods. */
export interface CatalogClient {
  listLinesheets: Read<WithAnyOfText<ListLinesheetsInput>, MatchingPage<LinesheetSummary>>;
  getLinesheet: Read<GetLinesheetInput, LinesheetSummary>;
  checkLinesheet: Read<CheckLinesheetInput, LinesheetCheck>;
  getLinesheetHistory: Read<GetLinesheetHistoryInput, CursorPage<AuditEntry>>;
  getLinesheetOptions: Read<Record<string, never>, LinesheetOptions>;
  createLinesheet: Write<CreateLinesheetInput, LinesheetSummary>;
  updateLinesheet: Write<UpdateLinesheetInput, LinesheetSummary>;
  submitLinesheet: Write<LinesheetActionInput, LinesheetSummary>;
  listLinesheetLines: Read<ListLinesheetLinesInput, CursorPage<LinesheetLine>>;
  getLineOptions: Read<Record<string, never>, LineOptions>;
  saveLinesheetLines: Write<SaveLinesheetLinesInput, { lines: LinesheetLine[] }>;
  removeLinesheetLines: Write<RemoveLinesheetLinesInput, { removed: number }>;
  requestLineDeletion: Write<RequestLineDeletionInput, { lines: LinesheetLine[] }>;
  listCarryoverCandidates: Read<ListCarryoverCandidatesInput, CursorPage<CarryoverCandidate>>;
  changeLineCarryover: Write<ChangeLineCarryoverInput, { lines: LinesheetLine[] }>;
  listAttributeTasks: Read<ListAttributeTasksInput, Page<AttributeTask>>;
  listAttributeCompleteness: Read<ListAttributeCompletenessInput, Page<AttributeCompleteness>>;
  getAttributeTask: Read<GetAttributeTaskInput, AttributeTask>;
  listTaskSkus: Read<ListTaskSkusInput, CursorPage<SkuAttributes>>;
  getAttributeOptions: Read<Record<string, never>, AttributeOptions>;
  saveSkuAttributes: Write<SaveSkuAttributesInput, TaskSkus>;
  submitAttributes: Write<AttributeTaskActionInput, AttributeTask>;
  importLinesheetLines: Write<ImportLinesheetLinesInput, ImportBatch>;
  importSkuAttributes: Write<ImportSkuAttributesInput, ImportBatch>;
  listImports: Read<ListImportsInput, CursorPage<ImportBatch>>;
  listImportErrors: Read<ListImportErrorsInput, CursorPage<ImportError>>;
  /** Not the catalog's: the file of an import, stored first in the folder `imports` of the files worker (ADR-069). */
  uploadImportFile: (file: ChosenFile) => Promise<void>;
  listProductImages: Read<ListProductImagesInput, ProductImages>;
  addProductImages: Write<AddProductImagesInput, ProductImages>;
  reorderProductImages: Write<ReorderProductImagesInput, ProductImages>;
  removeProductImage: Write<RemoveProductImageInput, ProductImages>;
  /** The bytes of an image, shown from a `blob:` URL (ADR-132, ADR-213). */
  downloadProductImage: Read<DownloadProductImageInput, Blob>;
  /** Not the catalog's: an image, stored first in the folder `product-images` of the files worker (ADR-213). */
  uploadProductImageFile: (file: ChosenFile) => Promise<void>;
}

let registered: CatalogClient | undefined;

/** Once, when the app starts: before it, no catalog screen loads (the route loaders read through it too). */
export function registerCatalogClient(client: CatalogClient): void {
  registered = client;
}

export function catalogClient(): CatalogClient {
  if (!registered) throw new Error("registerCatalogClient was not called when the app started");
  return registered;
}
